import type { Components } from '@/api/generated/api';
import { useDisplayName } from '@/composables/useDisplayName';
import { useStudentSelectionLabel } from '@/composables/useStudentSelectionLabel';
import { EKeyboardKey } from '@/utils/menu.utils';
import { getPerformanceAssessmentName } from '@/utils/performance.utils';
import { matchesAllSearchWords } from 'common/utils/string.utils';
import { computed, ref, watch, type ComputedRef, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { EQuickEntryCommandTrigger, EQuickEntrySuggestionKind } from './quickEntry.enums';
import {
  consumeCommandToken,
  parseCommandToken,
  type ICommandToken,
  type IQuickEntryFixedContext,
} from './quickEntry.utils';

export interface IQuickEntrySuggestionItem {
  id: string;
  label: string;
  kind: EQuickEntrySuggestionKind;
  performanceAssessment?: Components.Schemas.PerformanceAssessmentDto;
  schoolSubject?: Components.Schemas.SchoolSubjectDto;
  student?: Components.Schemas.StudentWithSubjectAccessDto;
}

interface IQuickEntrySuggestionSources {
  eligiblePerformanceAssessments: Ref<Components.Schemas.PerformanceAssessmentDto[]>;
  allSchoolSubjects: Ref<Components.Schemas.SchoolSubjectDto[]>;
  allStudents: Ref<Components.Schemas.StudentWithSubjectAccessDto[]>;
  eligibleStudents: Ref<Components.Schemas.StudentWithSubjectAccessDto[]>;
  selectedStudentIds: Ref<number[]>;
  allEligibleStudentsSelected: Ref<boolean>;
  fixedContext?: Ref<IQuickEntryFixedContext | undefined>;
}

interface IQuickEntrySuggestionActions {
  selectNote: () => void;
  selectPerformanceAssessment: (performanceAssessmentId: number) => void;
  selectSupportMeasure: () => boolean;
  selectSubject: (schoolSubjectId: number) => void;
  addStudent: (studentId: number) => void;
  selectAllEligibleStudents: () => void;
}

interface IQuickEntrySuggestionsComposable {
  isOpen: ComputedRef<boolean>;
  items: ComputedRef<IQuickEntrySuggestionItem[]>;
  activeIndex: Ref<number>;
  activeToken: Ref<ICommandToken | null>;
  updateFromInput: (text: string, caretIndex: number) => void;
  handleSuggestionKeydown: (event: KeyboardEvent) => boolean;
  acceptItem: (item: IQuickEntrySuggestionItem) => { text: string; caretIndex: number } | null;
  close: () => void;
}

export const useQuickEntrySuggestions = (
  sources: IQuickEntrySuggestionSources,
  actions: IQuickEntrySuggestionActions,
  options: { enabled?: Ref<boolean> } = {},
): IQuickEntrySuggestionsComposable => {
  const { t } = useI18n();
  const { getStudentDisplayName } = useDisplayName();
  const enabled = computed(() => options.enabled?.value ?? true);

  const activeToken = ref<ICommandToken | null>(null);
  const activeIndex = ref(0);
  const currentText = ref('');
  const currentCaretIndex = ref(0);

  // one flat command list: note first, assessments, the students/subject/competence selectors, then the subjects
  const commandItems = computed<IQuickEntrySuggestionItem[]>(() => {
    const fixedContext = sources.fixedContext?.value;
    return [
      ...(fixedContext?.performanceAssessmentId === undefined
        ? [
            { id: 'note', label: t('quickEntry.typeNote'), kind: EQuickEntrySuggestionKind.Note } as const,
            ...sources.eligiblePerformanceAssessments.value.map((performanceAssessment) => ({
              id: `performance-assessment-${performanceAssessment.id}`,
              label: getPerformanceAssessmentName(performanceAssessment, t),
              kind: EQuickEntrySuggestionKind.PerformanceAssessment,
              performanceAssessment,
            })),
            {
              id: 'support-measure',
              label: t('quickEntry.typeSupportMeasure'),
              kind: EQuickEntrySuggestionKind.SupportMeasure,
            } as const,
          ]
        : []),
      ...(fixedContext?.studentIds === undefined
        ? [
            {
              id: 'open-students',
              label: t('quickEntry.commandOpenStudents'),
              kind: EQuickEntrySuggestionKind.OpenStudents,
            } as const,
          ]
        : []),
      ...(fixedContext?.schoolSubjectId === undefined
        ? [
            {
              id: 'open-subject',
              label: t('quickEntry.commandOpenSubject'),
              kind: EQuickEntrySuggestionKind.OpenSubject,
            } as const,
          ]
        : []),
      ...(fixedContext?.competenceIds === undefined
        ? [
            {
              id: 'open-competences',
              label: t('quickEntry.commandOpenCompetences'),
              kind: EQuickEntrySuggestionKind.OpenCompetences,
            } as const,
          ]
        : []),
      ...(fixedContext?.schoolSubjectId === undefined
        ? sources.allSchoolSubjects.value.map((schoolSubject) => ({
            id: `subject-${schoolSubject.id}`,
            label: schoolSubject.name,
            kind: EQuickEntrySuggestionKind.Subject,
            schoolSubject,
          }))
        : []),
    ];
  });

  const { label: selectAllLabel } = useStudentSelectionLabel(sources.eligibleStudents, sources.allStudents, () =>
    t('students.allEligible', { count: sources.eligibleStudents.value.length }),
  );
  const allEligibleStudentsItems = computed<IQuickEntrySuggestionItem[]>(() => {
    if (sources.allEligibleStudentsSelected.value || sources.eligibleStudents.value.length < 2) return [];
    return [
      {
        id: 'all-eligible-students',
        label: selectAllLabel.value,
        kind: EQuickEntrySuggestionKind.AllEligibleStudents,
      },
    ];
  });

  const studentItems = computed<IQuickEntrySuggestionItem[]>(() => {
    if (sources.fixedContext?.value?.studentIds !== undefined) return [];
    const selectedIds = new Set(sources.selectedStudentIds.value);
    return [
      ...allEligibleStudentsItems.value,
      ...sources.eligibleStudents.value
        .filter((student) => !selectedIds.has(student.id))
        .map((student) => ({
          id: `student-${student.id}`,
          label: getStudentDisplayName(student),
          kind: EQuickEntrySuggestionKind.Student,
          student,
        })),
    ];
  });

  const items = computed<IQuickEntrySuggestionItem[]>(() => {
    const token = activeToken.value;
    if (!token) return [];

    const source = token.trigger === EQuickEntryCommandTrigger.Student ? studentItems.value : commandItems.value;
    const query = token.query.trim();
    return query ? source.filter((item) => matchesAllSearchWords([item.label], query)) : source;
  });

  const isOpen = computed(() => !!activeToken.value && items.value.length > 0);

  const close = (): void => {
    activeToken.value = null;
    activeIndex.value = 0;
  };

  watch(
    enabled,
    (isEnabled) => {
      if (!isEnabled) close();
    },
    { flush: 'sync' },
  );

  const updateFromInput = (text: string, caretIndex: number): void => {
    currentText.value = text;
    currentCaretIndex.value = caretIndex;

    const token = enabled.value ? parseCommandToken(text, caretIndex) : null;
    if (!token) {
      close();
      return;
    }

    if (!activeToken.value || activeToken.value.startIndex !== token.startIndex) activeIndex.value = 0;
    activeToken.value = token;
    if (activeIndex.value >= items.value.length) activeIndex.value = 0;
  };

  // applies the picked entity and consumes the token; opening the competence or
  // student selection is left to the caller (it owns the popovers)
  const acceptItem = (item: IQuickEntrySuggestionItem): { text: string; caretIndex: number } | null => {
    const token = activeToken.value;
    if (!token) return null;

    // A product-gated support-measure selection must leave the complete draft,
    // including the slash token, untouched when checkout is opened instead.
    if (item.kind === EQuickEntrySuggestionKind.SupportMeasure && !actions.selectSupportMeasure()) return null;

    const consumed = consumeCommandToken(currentText.value, currentCaretIndex.value, token);

    if (item.kind === EQuickEntrySuggestionKind.Note) actions.selectNote();
    else if (item.kind === EQuickEntrySuggestionKind.Student && item.student) actions.addStudent(item.student.id);
    else if (item.kind === EQuickEntrySuggestionKind.AllEligibleStudents) actions.selectAllEligibleStudents();
    else if (item.kind === EQuickEntrySuggestionKind.PerformanceAssessment && item.performanceAssessment) {
      actions.selectPerformanceAssessment(item.performanceAssessment.id);
    } else if (item.kind === EQuickEntrySuggestionKind.Subject && item.schoolSubject) {
      actions.selectSubject(item.schoolSubject.id);
    }

    close();
    currentText.value = consumed.text;
    currentCaretIndex.value = consumed.caretIndex;
    return consumed;
  };

  // navigation only — accepting (Enter/Tab) is handled by the caller so it can
  // write the consumed text back into the textarea
  const handleSuggestionKeydown = (event: KeyboardEvent): boolean => {
    if (!isOpen.value) return false;

    const total = items.value.length;

    if (event.key === EKeyboardKey.Down) {
      event.preventDefault();
      activeIndex.value = (activeIndex.value + 1) % total;
      return true;
    }
    if (event.key === EKeyboardKey.Up) {
      event.preventDefault();
      activeIndex.value = (activeIndex.value - 1 + total) % total;
      return true;
    }
    if (event.key === EKeyboardKey.Escape) {
      event.preventDefault();
      event.stopPropagation();
      close();
      return true;
    }

    return false;
  };

  return {
    isOpen,
    items,
    activeIndex,
    activeToken,
    updateFromInput,
    handleSuggestionKeydown,
    acceptItem,
    close,
  };
};
